import { isApiError } from '@fuze/portal-core';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { apiFetch } from '@/api/mutator';

// The browser goes here next, so only an https URL (Auth0's logout) gets through.
const logoutResponseSchema = z.object({ logoutUrl: z.url({ protocol: /^https$/ }) });

/**
 * Signs out: `POST /auth/logout` revokes the server session and clears its cookie, and returns Auth0's logout URL for
 * the browser to finish at. The API refuses `GET /auth/logout` (405), so this is a request, not a navigation; the
 * fetcher adds the CSRF marker it requires, as on every mutation.
 */
export const logOut = async (): Promise<string> => {
  const body = await apiFetch<unknown>('/auth/logout', { method: 'POST' });
  return logoutResponseSchema.parse(body).logoutUrl;
};

const isUnauthorized = (error: unknown) => isApiError(error) && error.status === 401;

/**
 * Log out as a mutation: on success the browser leaves for Auth0's logout URL. A 401 means there was no session to
 * revoke (expired, or ended in another tab), so the user is already signed out, not expired mid-use: this mutation
 * handles it itself and goes to the logged-out login page. Any other failure is thrown to the route's error screen,
 * which reports it.
 */
export const useLogOut = () =>
  useMutation({
    mutationFn: logOut,
    meta: { handlesUnauthorized: true },
    throwOnError: (error) => !isUnauthorized(error),
    onSuccess: (logoutUrl) => {
      window.location.assign(logoutUrl);
    },
    onError: (error) => {
      if (isUnauthorized(error)) {
        window.location.assign('/login?reason=logged_out');
      }
    },
  });
